import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableProductItemWeightAndDimensions } from '../../../../../../feature-flags/hooks/use-enable-product-item-weight-and-dimensions';
import { ProductItemWeightName, ProductItemDimensionsName } from '../../../../../../products/product-details/constants/product-options';
import Global from '../../../../../../store/global-selectors';
import { bulkLoadProductData } from '../../../../../../store/products/products.actions';

import { UnitConversionService } from '../../../../../../services/unit-conversion-service';
import { convertDimensions } from '../../../../../../services/unit-services/dimensions/dimension-convert-service';
import { convertWeight } from '../../../../../../services/unit-services/weights/weight-convert-service';

type ProductOption = {
  name: string;
  value?: string;
};

type ProductItemWeightJson = {
  value?: number;
  unit?: string;
};

type ProductItemDimensionsJson = {
  length?: number;
  width?: number;
  height?: number;
  unit?: string;
};

const safeConvertValue = (value: number, fromUnit: string | undefined, toUnit: string | undefined): number => {
  if (!fromUnit || !toUnit) return value;
  if (fromUnit.toLowerCase() === toUnit.toLowerCase()) return value;
  const converted = UnitConversionService.convertValue(value, fromUnit, toUnit);
  return Number.isNaN(converted) ? value : converted;
};

const parseProductItemWeight = (productOptions: ProductOption[]): ProductItemWeightJson | null => {
  const option = productOptions?.find((opt) => opt.name === ProductItemWeightName)?.value;
  if (!option) return null;
  try {
    return JSON.parse(option) as ProductItemWeightJson;
  } catch {
    return null;
  }
};

const parseProductItemDimensions = (productOptions: ProductOption[]): ProductItemDimensionsJson | null => {
  const option = productOptions?.find((opt) => opt.name === ProductItemDimensionsName)?.value;
  if (!option) return null;
  try {
    return JSON.parse(option) as ProductItemDimensionsJson;
  } catch {
    return null;
  }
};

type UseOrderDataReturn = {
  order: { orderId?: string; items: Record<string, unknown>[] };
  availablePackages: Array<Record<string, unknown>>;
  sellerDimensionUnit: string;
  sellerWeightUnit: string;
};

export const useOrderData = (fulfillmentPlanId: string): UseOrderDataReturn => {
  const dispatch = useDispatch();
  const isProductItemDimensionsEnabled = useEnableProductItemWeightAndDimensions();

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const allPackages = useSelector((state) => Global.selectSellerPackages(state)) ?? emptyArray;
  const sellerDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) ?? 'Inches';
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit) ?? 'Ounces';

  const selectedServiceId = fulfillmentPlan?.serviceId;
  const selectedService = useSelector((state) => {
    if (!selectedServiceId) return null;
    const services = Global.selectSellerServices(state) ?? emptyArray;
    return services.find((s: { serviceId: string }) => s.serviceId === selectedServiceId);
  });

  const availablePackages = useMemo(() => {
    const filtered = !selectedService?.carrierId
      ? allPackages.filter((pkg) => pkg.isCustom && !pkg.isHidden)
      : allPackages.filter((pkg) => {
          if (pkg.isHidden) return false;
          return pkg.isCustom || pkg.carrierId === selectedService.carrierId;
        });

    // Package dimensions/weights are stored in Inches/Ounces — convert to seller's unit
    return filtered.map((pkg: Record<string, unknown>) => {
      const dims = convertDimensions(pkg.dimensions as Record<string, unknown>, sellerDimensionUnit);
      const maxWeight = pkg.maxWeight ? convertWeight(pkg.maxWeight as Record<string, unknown>, sellerWeightUnit) : pkg.maxWeight;
      return { ...pkg, dimensions: dims, length: dims.length, width: dims.width, height: dims.height, maxWeight };
    });
  }, [allPackages, selectedService, sellerDimensionUnit, sellerWeightUnit]);

  const productIds = useMemo(() => {
    const fpItems = fulfillmentPlan?.items ?? emptyArray;
    return fpItems.map((item: { productId?: string }) => item.productId).filter(Boolean);
  }, [fulfillmentPlan?.items]);

  const products = useSelector((state) => (productIds.length > 0 ? Global.selectEntitiesProductsByIds(state, { productIds }) : emptyArray));

  useEffect(() => {
    if (productIds.length > 0) {
      const hasMissingProducts = products.some((p: unknown) => !p);
      if (hasMissingProducts) {
        dispatch(bulkLoadProductData(productIds));
      }
    }
  }, [dispatch, productIds, products]);

  const order = useMemo(() => {
    const fpItems = fulfillmentPlan?.items ?? emptyArray;
    const productsMap = Object.fromEntries(
      (products ?? emptyArray)
        .filter((product: { productId?: string }) => product?.productId)
        .map((product: { productId?: string }) => [product.productId, product]),
    );

    const enrichedItems = fpItems.map((item: Record<string, unknown>) => {
      const product = productsMap[item.productId as string] as Record<string, unknown> | undefined;

      let itemLength = (product?.length as number) ?? (item.length as number) ?? 0;
      let itemWidth = (product?.width as number) ?? (item.width as number) ?? 0;
      let itemHeight = (product?.height as number) ?? (item.height as number) ?? 0;
      let weightValue = (item.weight as { value?: number })?.value ?? (item.weight as number) ?? (product?.weight as number) ?? 0;

      if (isProductItemDimensionsEnabled && product?.productOptions) {
        const productOptions = product.productOptions as ProductOption[];

        const parsedDimensions = parseProductItemDimensions(productOptions);
        if (parsedDimensions && parsedDimensions.length && parsedDimensions.width && parsedDimensions.height) {
          itemLength = safeConvertValue(parsedDimensions.length, parsedDimensions.unit, sellerDimensionUnit);
          itemWidth = safeConvertValue(parsedDimensions.width, parsedDimensions.unit, sellerDimensionUnit);
          itemHeight = safeConvertValue(parsedDimensions.height, parsedDimensions.unit, sellerDimensionUnit);
        }

        const parsedWeight = parseProductItemWeight(productOptions);
        if (parsedWeight?.value && parsedWeight.value > 0) {
          weightValue = safeConvertValue(parsedWeight.value, parsedWeight.unit, sellerWeightUnit);
        }
      }

      return {
        ...item,
        length: itemLength,
        width: itemWidth,
        height: itemHeight,
        weight: { value: weightValue, unit: sellerWeightUnit },
      };
    });

    return {
      orderId: fulfillmentPlan?.orderId,
      items: enrichedItems,
    };
  }, [fulfillmentPlan, products, sellerWeightUnit, sellerDimensionUnit, isProductItemDimensionsEnabled]);

  return { order, availablePackages, sellerDimensionUnit, sellerWeightUnit };
};
